import React, { useContext } from 'react';
import { DashboardContext } from '../../../context/DashboardContext';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Trophy,
  CreditCard
} from 'lucide-react';
import { formatCurrency } from '../../../lib/utils';

export default function HeroCards() {
  const { dashboardData } = useContext(DashboardContext);
  const { loading } = dashboardData || { loading: true };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 shadow-md animate-pulse h-36">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
            <div className="h-6 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  // Read values
  const selectedRange = dashboardData?.selectedRange;
  const todayRevenue = Number(dashboardData?.todayRevenue || 0);
  const yesterdayRevenue = Number(dashboardData?.yesterdayRevenue || 0);
  const todayOrders = Number(dashboardData?.todayOrders || 0);
  const yesterdayOrders = Number(dashboardData?.yesterdayOrders || 0);
  const paymentSplit = dashboardData?.paymentSplit || { cash: 0, online: 0 };

  const topDish = dashboardData?.topDish || {
    name: "N/A",
    quantity: 0,
    change: 0
  };

  const revenueChange = dashboardData.revenueChange ?? 0;
  const ordersChange = dashboardData.ordersChange ?? 0;

  const totalPayments =
    (paymentSplit.cash || 0) + (paymentSplit.online || 0);

  const cashPercentage =
    totalPayments > 0
      ? Math.round((paymentSplit.cash / totalPayments) * 100)
      : 0;

  const onlinePercentage = 100 - cashPercentage;

  // -----------------------------
  // DYNAMIC RANGE SUFFIX TEXT
  // -----------------------------
  const getPeriodSuffix = () => {
    switch (selectedRange) {
      case "today":
        return "today";
      case "yesterday":
        return "yesterday";
      case "last7days":
        return "in last 7 days";
      case "last30days":
        return "in last 30 days";
      default:
        return "";
    }
  };

  const getComparisonLabel = () => {
    switch (selectedRange) {
      case "today":
        return "yesterday";
      case "yesterday":
        return "previous day";
      case "last7days":
        return "last 7 days";
      case "last30days":
        return "last 30 days";
      default:
        return "previous period";
    }
  };

  const periodSuffix = getPeriodSuffix();
  const comparisonLabel = getComparisonLabel();

  // -----------------------------
  // HERO CARDS DATA
  // -----------------------------
  const cards = [
    {
      id: 'revenue',
      title:
        selectedRange === "today"
          ? "Today's Revenue"
          : selectedRange === "yesterday"
          ? "Yesterday's Revenue"
          : "Revenue",
      value: formatCurrency(todayRevenue),
      meta: `${formatCurrency(yesterdayRevenue)} ${comparisonLabel}`,
      change: revenueChange,
      icon: TrendingUp,
      color: 'bg-gradient-to-r from-emerald-500 to-green-600',
    },
    {
      id: 'orders',
      title:
        selectedRange === "today"
          ? "Total Orders Today"
          : selectedRange === "yesterday"
          ? "Total Orders Yesterday"
          : "Total Orders",
      value: todayOrders.toString(),
      meta: `${yesterdayOrders} ${comparisonLabel}`,
      change: ordersChange,
      icon: ShoppingCart,
      color: 'bg-gradient-to-r from-sky-500 to-blue-600',
    },
    {
      id: 'topDish',
      title: 'Top Selling Dish',
      value: topDish.name,
      meta: `${topDish.quantity} sold ${periodSuffix}`,
      change: topDish.change ?? 0,
      icon: Trophy,
      color: 'bg-gradient-to-r from-amber-400 to-orange-600',
    },
    {
      id: 'payment',
      title: 'Payment Split',
      value: `${cashPercentage}% Cash`,
      meta: `${onlinePercentage}% Online`,
      icon: CreditCard,
      color: 'bg-gradient-to-r from-violet-400 to-purple-600',
      noChange: true,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {cards.map((c) => (
        <div
          key={c.id}
          className="relative bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 h-36 group"
        >
          <div className={`h-2 ${c.color}`}></div>

          <div className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-center justify-between mb-1">
              <div className={`p-2.5 rounded-lg ${c.color} text-white shadow-sm group-hover:scale-105 transition-transform`}>
                <c.icon className="w-4 h-4" />
              </div>
              {!c.noChange && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    c.change > 0
                      ? 'bg-emerald-100 text-emerald-700'
                      : c.change < 0
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {c.change > 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  <span>{Math.abs(c.change).toFixed(2)}%</span>
                </span>
              )}
            </div>

            <div>
              <h3 className="text-xs font-medium uppercase text-gray-500 mb-0.5">{c.title}</h3>
              <p className="text-xl font-semibold text-gray-900 leading-tight">{c.value}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{c.meta}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
